import { openaiConfig } from "../config/openaiConfig.js";
import { apiConfig } from "../config/apiConfig.js";
import { getApiToken } from "./authService.js";

export class OpenAIService {
    constructor(apiKey = null, userId = null) {
        this.apiKey = apiKey;
        this.userId = userId;
        this.endpoint = openaiConfig.endpoint;
        this.deploymentName = openaiConfig.deploymentName;
        this.apiVersion = openaiConfig.apiVersion;
        this.temperature = openaiConfig.temperature;
        this.useServerProxy = !apiKey;
    }

    async chat(messages, responseFormat = null, estimatedOutputTokens = null) {
        let url;
        let headers = {
            "Content-Type": "application/json"
        };
        
        if (this.useServerProxy) {
            // Use the proxied API endpoint
            url = apiConfig.getUrl('chat');
            try { headers["Authorization"] = `Bearer ${await getApiToken()}`; } catch (e) { throw new Error(`Authentifizierung fehlgeschlagen: ${e.message}`); }
        } else {
            url = `${this.endpoint}/openai/deployments/${this.deploymentName}/chat/completions?api-version=${this.apiVersion}`;
            headers["api-key"] = this.apiKey;
        }
        
        try {
            const response = await fetch(url, {
                method: "POST",
                headers: headers,
                body: JSON.stringify({
                    messages: messages,
                    userId: this.userId,
                    temperature: this.temperature,
                    deploymentName: this.deploymentName,
                    responseFormat: responseFormat,
                    estimatedOutputTokens: estimatedOutputTokens
                })
            });

            if (!response.ok) {
                // Read the body once as text, then try to parse as JSON
                const errorText = await response.text();
                let errorData;
                try {
                    errorData = JSON.parse(errorText);
                    console.error('❌ API Error Response:', errorData);
                } catch {
                    console.error('❌ API Error (non-JSON):', errorText);
                    if (response.status === 504) {
                        throw new Error('❌ Zeitüberschreitung: Die Anfrage hat zu lange gedauert. Bitte wähle weniger Zeilen aus.');
                    }
                    throw new Error(response.statusText || `HTTP ${response.status}`);
                }
                
                // Handle quota errors (429) - TOKEN-BASED
                if (response.status === 429) {
                    let message;
                    
                    // Use server-provided message if available (it's already formatted)
                    if (errorData.message) {
                        // Translate to German
                        message = this._translateQuotaError(errorData);
                    } else if (errorData.reason === 'daily_token_limit_exceeded') {
                        const tokensRemaining = errorData.limit - errorData.current;
                        message = `Dein Tageslimit ist erreicht. Du hast ${errorData.current.toLocaleString('de-DE')} von ${errorData.limit.toLocaleString('de-DE')} Tokens verwendet. Verbleibend: ${tokensRemaining.toLocaleString('de-DE')} Tokens. Wird um Mitternacht zurückgesetzt.`;
                    } else if (errorData.reason === 'monthly_token_limit_exceeded') {
                        const tokensRemaining = errorData.limit - errorData.current;
                        message = `Dein Monatslimit ist erreicht. Du hast ${errorData.current.toLocaleString('de-DE')} von ${errorData.limit.toLocaleString('de-DE')} Tokens verwendet. Verbleibend: ${tokensRemaining.toLocaleString('de-DE')} Tokens. Wird nächsten Monat zurückgesetzt.`;
                    } else if (errorData.reason === 'request_too_large') {
                        message = `Deine Anfrage mit ca. ${errorData.current.toLocaleString('de-DE')} Tokens überschreitet dein Limit von ${errorData.limit.toLocaleString('de-DE')} Tokens pro Anfrage. Bitte wähle weniger Daten aus.`;
                    } else if (errorData.reason === 'license_expired') {
                        message = `Deine Lizenz ist abgelaufen${errorData.expiryDate ? ` (${new Date(errorData.expiryDate).toLocaleDateString('de-DE')})` : ''}. Bitte wende dich an deinen Administrator.`;
                    } else {
                        message = errorData.message || 'Kontingent überschritten';
                    }
                    
                    throw new Error(message);
                }
                
                // Handle access denied errors (403)
                if (response.status === 403) {
                    if (errorData.error === 'Access denied') {
                        throw new Error('Du bist nicht berechtigt, diesen Dienst zu nutzen.');
                    }
                }
                
                // Handle other errors (401, etc.)
                // Try to extract the actual Azure error from the details field
                let errorMessage = errorData.message || errorData.error || response.statusText || `HTTP ${response.status}`;
                if (errorData.details) {
                    try {
                        const azureError = typeof errorData.details === 'string' ? JSON.parse(errorData.details) : errorData.details;
                        const azureCode = azureError?.error?.code || '';
                        const azureMsg = azureError?.error?.message || '';
                        if (azureCode === 'context_length_exceeded' || azureMsg.includes('context')) {
                            errorMessage = `Die Datenmenge ist zu groß für dieses Modell. Bitte wähle weniger Zeilen aus oder wechsle zu einem Modell mit größerem Kontextfenster.`;
                        } else if (azureCode === 'max_completion_tokens_exceeded' || azureMsg.includes('max_completion_tokens') || azureMsg.includes('max_tokens')) {
                            errorMessage = `Das Token-Limit für die Antwort wurde für dieses Modell überschritten. Bitte wähle weniger Zeilen aus oder wechsle zu einem Modell mit größerem Kontextfenster.`;
                        } else if (azureMsg) {
                            errorMessage = `Azure OpenAI API error: ${azureMsg}`;
                        }
                    } catch { /* keep original errorMessage */ }
                }
                throw new Error(errorMessage);
            }

            const data = await response.json();
            
            return {
                message: data.choices[0].message,
                quota: data.quota
            };
        } catch (error) {
            console.error("Error calling OpenAI:", error);
            throw error;
        }
    }

    /**
     * Agentic loop with tool calling.
     * Sends messages with tool definitions, executes tool calls, feeds results back,
     * and repeats until the model stops or calls the designated finish tool.
     *
     * @param {Array} messages - Initial messages array
     * @param {Array} tools - OpenAI tool definitions
     * @param {Function} toolExecutor - async (name, args) => result (plain JS value)
     * @param {string} finishTool - Name of the tool whose call signals completion (its args become the return value)
     * @param {number} maxSteps - Safety cap on iterations
     * @returns {{ result: any, quota: any }} result = finishTool args, or last message content if model stops naturally
     */
    async chatWithTools(messages, tools, toolExecutor, finishTool = 'submit_operations', maxSteps = 8) {
        const history = [...messages];

        for (let step = 0; step < maxSteps; step++) {
            const response = await this._rawChat(history, { tools, tool_choice: 'auto' });
            const msg = response.message;
            console.log(`[EPJ agent step ${step}] tool_calls=${msg.tool_calls?.length ?? 0} content_length=${msg.content?.length ?? 0}`);
            history.push({ role: 'assistant', content: msg.content || null, tool_calls: msg.tool_calls });

            // No tool calls → model responded with text (tool calling not supported or ignored)
            // Try to parse the content as a JSON operations object before giving up
            if (!msg.tool_calls || msg.tool_calls.length === 0) {
                if (msg.content) {
                    try {
                        const parsed = JSON.parse(msg.content);
                        if (parsed?.operations) {
                            console.log('[EPJ agent] Fallback: parsed operations from text response');
                            return { result: parsed, quota: response.quota };
                        }
                    } catch { /* not JSON */ }
                }
                return { result: msg.content, quota: response.quota };
            }

            // Execute each tool call
            for (const tc of msg.tool_calls) {
                let args;
                try { args = JSON.parse(tc.function.arguments); } catch (e) { args = {}; console.warn('[EPJ agent] Failed to parse tool args:', tc.function.arguments, e); }
                console.log(`[EPJ agent] tool="${tc.function.name}" args=`, args);

                // Finish tool — return its arguments as the final result
                if (tc.function.name === finishTool) {
                    console.log('[EPJ agent] submit_operations called, operations=', args?.operations?.length);
                    if (!args?.operations || !Array.isArray(args.operations) || args.operations.length === 0) {
                        // Model forgot the operations array — tell it to retry
                        console.warn('[EPJ agent] submit_operations missing operations, asking model to retry');
                        history.push({
                            role: 'tool',
                            tool_call_id: tc.id,
                            content: JSON.stringify({ error: 'Das Feld "operations" fehlt oder ist leer. Bitte submit_operations erneut aufrufen und das "operations"-Array mit den konkreten Bearbeitungsbefehlen mitgeben.' })
                        });
                        break; // continue outer loop
                    }
                    return { result: args, quota: response.quota };
                }

                const toolResult = await toolExecutor(tc.function.name, args);
                console.log(`[EPJ agent] tool result for "${tc.function.name}":`, toolResult);
                history.push({
                    role: 'tool',
                    tool_call_id: tc.id,
                    content: JSON.stringify(toolResult)
                });
            }
        }
        throw new Error(`EPJ agent: max steps (${maxSteps}) exceeded without finishing`);
    }

    /**
     * Low-level chat call — sends messages + extra body params, returns { message, quota }.
     */
    async _rawChat(messages, extraBody = {}) {
        let url;
        let headers = { 'Content-Type': 'application/json' };

        if (this.useServerProxy) {
            url = apiConfig.getUrl('chat');
            try { headers['Authorization'] = `Bearer ${await getApiToken()}`; } catch (e) { throw new Error(`Authentifizierung fehlgeschlagen: ${e.message}`); }
        } else {
            url = `${this.endpoint}/openai/deployments/${this.deploymentName}/chat/completions?api-version=${this.apiVersion}`;
            headers['api-key'] = this.apiKey;
        }

        const body = {
            messages,
            userId: this.userId,
            temperature: this.temperature,
            deploymentName: this.deploymentName,
            ...extraBody
        };

        const response = await fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            let errorData;
            try { errorData = await response.json(); } catch { throw new Error(response.statusText || `HTTP ${response.status}`); }
            const msg = errorData.message || errorData.error || response.statusText || `HTTP ${response.status}`;
            throw new Error(msg);
        }

        const data = await response.json();
        return { message: data.choices[0].message, quota: data.quota };
    }

    _translateQuotaError(errorData) {
        // Translate English error messages from server to German
        const { reason, limit, current, required } = errorData;
        
        if (reason === 'daily_token_limit_exceeded') {
            const remaining = limit - current;
            return `Dein Tageslimit ist erreicht. Diese Anfrage benötigt ${required?.toLocaleString('de-DE') || '?'} Tokens, aber nur noch ${remaining.toLocaleString('de-DE')} Tokens verfügbar. Wird um Mitternacht zurückgesetzt.`;
        }
        
        if (reason === 'monthly_token_limit_exceeded') {
            const remaining = limit - current;
            return `Dein Monatslimit ist erreicht. Diese Anfrage benötigt ${required?.toLocaleString('de-DE') || '?'} Tokens, aber nur noch ${remaining.toLocaleString('de-DE')} Tokens verfügbar. Wird nächsten Monat zurückgesetzt.`;
        }
        
        if (reason === 'request_too_large') {
            return `Deine Anfrage mit ca. ${current.toLocaleString('de-DE')} Tokens überschreitet das Maximum von ${limit.toLocaleString('de-DE')} Tokens pro Anfrage. Bitte wähle weniger Daten aus.`;
        }

        if (reason === 'license_expired') {
            const expiry = errorData.expiryDate ? ` (${new Date(errorData.expiryDate).toLocaleDateString('de-DE')})` : '';
            return `Deine Lizenz ist abgelaufen${expiry}. Bitte wende dich an deinen Administrator.`;
        }
        
        return errorData.message || 'Kontingent überschritten';
    }

    // Helper method to format quota info for UI display
    formatQuotaInfo(quota) {
        if (!quota) return null;

        const quotaType = quota.type === 'domain' ? 'Gemeinsames Kontingent' : 'Persönliches Kontingent';
        
        return {
            type: quotaType,
            daily: {
                used: quota.daily.tokens_used.toLocaleString('de-DE'),
                limit: quota.daily.tokens_limit.toLocaleString('de-DE'),
                remaining: quota.daily.tokens_remaining.toLocaleString('de-DE'),
                percentage: Math.round((quota.daily.tokens_used / quota.daily.tokens_limit) * 100),
                requests: quota.daily.requests_made
            },
            monthly: {
                used: quota.monthly.tokens_used.toLocaleString('de-DE'),
                limit: quota.monthly.tokens_limit.toLocaleString('de-DE'),
                remaining: quota.monthly.tokens_remaining.toLocaleString('de-DE'),
                percentage: Math.round((quota.monthly.tokens_used / quota.monthly.tokens_limit) * 100),
                requests: quota.monthly.requests_made
            }
        };
    }
}